// src/pages/claim/ClaimTracker.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Trash2,
  Loader2,
  PlusCircle,
  MessageSquare,
  Eye,
} from "lucide-react";
import { Button } from "../../components/button/Button";
import { Input } from "../../components/textfields/InputField";
import ClaimUploadModal from "../../components/ClaimUploadModal";
import useAuth from "../../hooks/useAuth";
import { apiClient } from "../../utils/apiClient";
import { API as API_URLS } from "../../utils/apiUrls";

const ROWS_PER_PAGE_OPTIONS = [12, 24, 48];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const getStatusIcon = (status) => {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return <CheckCircle className="text-emerald-500" />;
    case "pending":
    case "under_review":
    case "under review":
      return <Clock className="text-amber-500" />;
    case "rejected":
      return <AlertTriangle className="text-red-500" />;
    default:
      return <span className="text-gray-400"><svg width="16" height="16" /></span>;
  }
};

const proofLabelMap = {
  unpacked_video: "Unpacked video",
  packaging_video: "Packaging video",
  proof_of_delivery_photo: "Proof of delivery",
};

const isVideoUrl = (u) => /\.(mp4|mov|webm|mkv|avi|ogg)(\?.*)?$/i.test(u || "");

// URL builders
const buildClaimsUrl = ({ base = process.env.REACT_APP_POCAS_BASE_URL, merchantId, limit, offset, q }) => {
  const baseUrl = (base || "").replace(/\/+$/, "");
  const params = new URLSearchParams();
  if (merchantId) params.set("merchantId", merchantId);
  if (limit !== undefined) params.set("limit", String(limit));
  if (offset !== undefined) params.set("offset", String(offset));
  if (q) params.set("q", q);
  return `${baseUrl}/v1/pocas/claims?${params.toString()}`;
};
const buildClaimProofsUrl = (claimId, base = process.env.REACT_APP_POCAS_BASE_URL) => {
  const baseUrl = (base || "").replace(/\/+$/, "");
  return `${baseUrl}/v1/pocas/claims/${encodeURIComponent(claimId)}/proofs`;
};
const buildClaimUrl = (claimId, base = process.env.REACT_APP_POCAS_BASE_URL) => {
  const baseUrl = (base || "").replace(/\/+$/, "");
  return `${baseUrl}/v1/pocas/claims/${encodeURIComponent(claimId)}`;
};

const formatAge = (iso) => {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  } catch {
    return iso;
  }
};

const proofTypesForClaim = (raw) => {
  if (!raw) return [];
  const keys = Object.keys(proofLabelMap);
  return keys.filter(k => {
    const v = raw[k] ?? raw[k.replace(/_/g, "")] ?? raw?.__raw?.[k];
    return !!v;
  });
};

// Normalizer
const normalizeClaimFromApi = (raw) => {
  if (!raw) return raw;
  const c = {};
  c.claim_id = raw.claim_id ?? raw.claimId ?? raw.claimIdStr ?? raw.claimId;
  c.merchant_id = raw.merchant_id ?? raw.merchantId ?? raw.merchantId;
  c.awb_number = raw.awb_number ?? raw.awbNumber ?? raw.awbNumber;
  c.order_reference_number = raw.order_reference_number ?? raw.orderReferenceNumber ?? raw.orderReferenceNumber;
  c.claim_status = raw.claim_status ?? raw.claimStatus ?? raw.claimStatus;
  c.claim_status_code = raw.claim_status_code ?? raw.claimStatusCode ?? raw.claimStatusCode;
  c.claim_description = raw.claim_description ?? raw.claimDescription ?? raw.claimDescription;
  c.claim_type = raw.claim_type ?? raw.claimType ?? raw.claimtype ?? null;
  c.claim_reason = raw.claim_reason ?? raw.claimReason ?? raw.claimReason;
  c.items = raw.items ?? raw.itemsList ?? raw.items;
  c.claim_dispute_history = raw.claim_dispute_history ?? raw.claimDisputeHistory ?? raw.claimDisputeHistory;
  c.unpacked_video = raw.unpacked_video ?? raw.unpackedVideo ?? raw.unpackedVideo;
  c.packaging_video = raw.packaging_video ?? raw.packagingVideo ?? raw.packagingVideo;
  c.proof_of_delivery_photo = raw.proof_of_delivery_photo ?? raw.proofOfDeliveryPhoto ?? raw.proofOfDeliveryPhoto;
  c.created_date_time = raw.created_date_time ?? raw.createdDateTime ?? raw.createdDateTime;
  c.updated_date_time = raw.updated_date_time ?? raw.updatedDateTime ?? raw.updatedDateTime;
  c.claim_initiated_date_time = raw.claim_initiated_date_time ?? raw.claimInitiatedDateTime ?? raw.claimInitiatedDateTime;
  c.operation_remarks = raw.operation_remarks ?? raw.operationRemarks ?? raw.operationRemarks;
  c.__raw = raw;
  return c;
};

const normalizeMessage = (raw) => {
  if (!raw) return null;
  return {
    messageId: raw.message_id ?? raw.messageId ?? raw.id ?? `tmp-${Date.now()}`,
    claimId: raw.claim_id ?? raw.claimId ?? null,
    authorId: raw.author_id ?? raw.authorId ?? null,
    authorRole: String(raw.author_role ?? raw.authorRole ?? "").toUpperCase(),
    text: raw.text ?? raw.message ?? raw.msg ?? raw.body ?? "",
    createdAt: raw.created_date_time ?? raw.createdDateTime ?? raw.timestamp ?? new Date().toISOString(),
    raw: raw,
  };
};

export default function ClaimTracker() {
  const { getAuthToken, getMerchantId } = useAuth();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  // search input & server query
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [limit, setLimit] = useState(24);
  const [offset, setOffset] = useState(0);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [proofsForClaim, setProofsForClaim] = useState({});
  const [proofsLoading, setProofsLoading] = useState({});
  const [errorMsg, setErrorMsg] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // conversation
  const [convoOpen, setConvoOpen] = useState(false);
  const [convoClaim, setConvoClaim] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convoLoading, setConvoLoading] = useState(false);
  const [convoText, setConvoText] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);

  const getAuthHeaders = () => {
    const token = getAuthToken?.();
    return token ? { Authorization: token } : {};
  };

  // fetch claims from server (server-side q)
  const fetchClaims = useCallback(async () => {
    setTableLoading(true);
    setErrorMsg(null);
    try {
      const merchantId = getMerchantId?.();
      const url = buildClaimsUrl({ merchantId, limit, offset, q: searchQuery || undefined });
      const res = await axios.get(url, { headers: getAuthHeaders(), timeout: 60000 });
      const rawData = Array.isArray(res.data) ? res.data : res.data?.data ?? res.data;
      const normalized = (rawData || []).map(normalizeClaimFromApi);
      setClaims(normalized);
    } catch (err) {
      console.error("fetchClaims error", err);
      setErrorMsg(err?.response?.data?.message ?? err?.message ?? "Failed to fetch claims");
      setClaims([]);
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  }, [getMerchantId, getAuthToken, limit, offset, searchQuery]);

  useEffect(() => {
    fetchClaims();
  }, [refreshKey, limit, offset, searchQuery]);

  const displayedClaims = claims;

  const fetchProofsForClaim = async (claimId) => {
    if (!claimId) return;
    setProofsLoading((p) => ({ ...p, [claimId]: true }));
    try {
      const url = buildClaimProofsUrl(claimId);
      const res = await axios.get(url, { headers: getAuthHeaders(), timeout: 20000 });
      setProofsForClaim((prev) => ({ ...prev, [claimId]: res.data?.data ? res.data.data : res.data }));
    } catch (err) {
      console.warn("fetchProofsForClaim failed", err);
      setProofsForClaim((prev) => {
        const copy = { ...prev };
        delete copy[claimId];
        return copy;
      });
    } finally {
      setProofsLoading((p) => ({ ...p, [claimId]: false }));
    }
  };

  const openProofFresh = async (claimId, label) => {
    try {
      const proofObj =
        proofsForClaim[claimId]?.[label] ??
        proofsForClaim[claimId]?.[label.toLowerCase()] ??
        selectedClaim?.[label] ??
        selectedClaim?.[label.toLowerCase()];

      if (!proofObj) {
        alert("No proof available.");
        return;
      }

      const uploadId =
        proofObj?.upload_id ??
        proofObj?.uploadId ??
        proofObj?.claim_proof_object_key?.split("/")?.pop();

      if (!uploadId) {
        alert("Upload ID missing for this proof.");
        return;
      }

      const merchantId = getMerchantId?.();
      const url = API_URLS.getUploadRefreshClaimPresignedUrlByUploadId(uploadId, merchantId);
      const resp = await apiClient.get(url, { headers: getAuthHeaders(), timeout: 20000 });
      const refreshed = resp.data?.data ?? resp.data;

      if (refreshed?.url) {
        window.open(refreshed.url, "_blank", "noopener,noreferrer");
      } else {
        alert("No proof URL available. Try refreshing proofs.");
      }
    } catch (err) {
      console.error("openProofFresh failed", err);
      alert("Failed to open proof. Try refreshing proofs.");
    }
  };


  const selectAndOpenDetails = async (claim) => {
    setSelectedClaim(claim);
    await fetchProofsForClaim(claim.claim_id);
    setTimeout(() => {
      const el = document.getElementById("claim-detail-panel");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const deleteClaim = async (claimId) => {
    if (!window.confirm("Are you sure you want to delete this claim? This cannot be undone.")) return;
    try {
      setDeletingId(claimId);
      const url = buildClaimUrl(claimId);
      await axios.delete(url, { headers: getAuthHeaders(), params: { merchantId: getMerchantId?.() } });
      setClaims((prev) => prev.filter((c) => c.claim_id !== claimId));
      if (selectedClaim?.claim_id === claimId) setSelectedClaim(null);
    } catch (err) {
      console.error("deleteClaim error", err);
      alert("Failed to delete claim: " + (err?.response?.data?.message ?? err?.message ?? "Unknown"));
    } finally {
      setDeletingId(null);
    }
  };

  const openConversation = async (claim) => {
    setConvoClaim(claim);
    setConvoOpen(true);
    setConversations([]);
    setConvoLoading(true);
    try {
      const merchantId = getMerchantId?.();
      const body = { user_id: merchantId, user_role: "MERCHANT", limit: 100, offset: 0 };

      const res = await axios.post(API_URLS.claimMessagesList(claim.claim_id), body, {
        headers: getAuthHeaders(),
        timeout: 20000,
      });

      const data = Array.isArray(res.data) ? res.data : res.data?.data ?? res.data ?? [];
      const normalized = (Array.isArray(data) ? data : []).map(normalizeMessage).filter(Boolean);
      normalized.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setConversations(normalized);
    } catch (err) {
      console.warn("load conversations failed", err);
      setConversations([]);
    } finally {
      setConvoLoading(false);
    }
  };

  const sendConversationMessage = async (text) => {
    if (!convoClaim) return;
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    const merchantId = getMerchantId?.();
    const optimistic = {
      messageId: `tmp-${Date.now()}`,
      claimId: convoClaim.claim_id,
      authorId: merchantId,
      authorRole: "MERCHANT",
      text: trimmed,
      createdAt: new Date().toISOString(),
      read: false,
      optimistic: true,
    };
    setConversations((c) => [...c, optimistic]);
    setConvoText("");

    try {
      const payload = { text: trimmed, author_id: merchantId, author_role: "MERCHANT" };
      const res = await axios.post(API_URLS.postClaimMessage(convoClaim.claim_id), payload, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });

      const createdRaw = res.data?.data ?? res.data;
      const created = normalizeMessage(createdRaw) || createdRaw;

      setConversations((prev) => {
        let replaced = false;
        const mapped = prev.map((m) => {
          if (!replaced && m.optimistic && m.text === optimistic.text && m.authorId === optimistic.authorId) {
            replaced = true;
            return created;
          }
          return m;
        });
        if (!replaced) mapped.push(created);
        mapped.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        return mapped;
      });
    } catch (err) {
      console.warn("send message failed", err);
      setConversations((prev) =>
        prev.map((m) => (m.optimistic && m.text === trimmed ? { ...m, failed: true } : m))
      );
    }
  };

  // replace existing saveClaimEdits with this snake_case implementation
  const saveClaimEdits = async (claimId, patches) => {
    try {
      const claim = claims.find((c) => c.claim_id === claimId) || selectedClaim;
      if (!claim) throw new Error("Claim not found locally");

      const body = {
        // required always
        claim_id: claim.claim_id,
        merchant_id: getMerchantId?.() ?? claim.merchant_id,
        awb_number: claim.awb_number,
        order_reference_number: claim.order_reference_number,

        claim_code: patches.claimCode ?? patches.claim_code ?? claim.claim_code ?? claim.claimCode,
        claim_reason: patches.claim_reason ?? patches.claimReason ?? claim.claim_reason ?? claim.claimReason,
        claim_description: patches.claim_description ?? patches.claimDescription ?? claim.claim_description ?? claim.claimDescription,

        // items: map to sku_id, item_name, units
        items:
          (patches.items ?? claim.items ?? null)
            ? (patches.items ?? claim.items).map((it) => {
              return {
                sku_id: it.sku_id ?? it.skuId,
                item_name: it.item_name ?? it.itemName ?? undefined,
                units: it.units ?? it.quantity ?? undefined,
              };
            })
            : undefined,

        claim_action_taken_by: patches.claimActionTakenBy ?? patches.claim_action_taken_by ?? undefined,
        operation_remarks: patches.operationRemarks ?? patches.operation_remarks ?? claim.operation_remarks ?? claim.operationRemarks,

        proof_upload_id: patches.proofUploadId ?? patches.proof_upload_id ?? undefined,
        unboxing_upload_id: patches.unboxingUploadId ?? patches.unboxing_upload_id ?? undefined,
        packaging_upload_id: patches.packagingUploadId ?? patches.packaging_upload_id ?? undefined,
        updated_at: new Date().toISOString(),
      };

      const payload = Object.entries(body).reduce((acc, [k, v]) => {
        if (v === undefined || v === null) return acc;
        if (Array.isArray(v) && v.length === 0) return acc;
        if (k === "items") {
          const cleanedItems = v
            .map((it) =>
              Object.entries(it).reduce((iiAcc, [ik, iv]) => {
                if (iv !== undefined && iv !== null) iiAcc[ik] = iv;
                return iiAcc;
              }, {})
            )
            .filter((it) => Object.keys(it).length > 0);
          if (cleanedItems.length === 0) return acc;
          acc[k] = cleanedItems;
          return acc;
        }
        acc[k] = v;
        return acc;
      }, {});

      const url = buildClaimUrl(claimId);
      const resp = await axios.put(url, payload, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        timeout: 20000,
      });

      const updated = resp.data?.data ?? resp.data ?? null;
      if (updated) {
        const norm = normalizeClaimFromApi(updated);
        setClaims((prev) => prev.map((c) => (c.claim_id === claimId ? norm : c)));
        if (selectedClaim?.claim_id === claimId) setSelectedClaim(norm);
      } else {
        // optimistic local patch
        setClaims((prev) => prev.map((c) => (c.claim_id === claimId ? { ...c, ...patches } : c)));
        if (selectedClaim?.claim_id === claimId) setSelectedClaim((s) => ({ ...s, ...patches }));
      }

      return true;
    } catch (err) {
      console.error("Failed to save claim edits", err);
      alert("Failed to save changes: " + (err?.response?.data?.message ?? err?.message ?? "Unknown"));
      return false;
    }
  };



  const onRefresh = () => setRefreshKey((k) => k + 1);

  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td colSpan={6} className="p-4">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
      </td>
    </tr>
  );

  const renderProofPreview = (claimId, label, proofObj) => {
    if (!proofObj) return null;
    const key = `${claimId}_${label}`;

    const thumb =
      proofObj.thumbnail_url ??
      proofObj.thumbnailUrl ??
      proofObj.preview_url ??
      proofObj.previewUrl ??
      proofObj.poster ??
      proofObj.poster_url ??
      proofObj.previewImageUrl ??
      proofObj.preview_image_url;

    const url = proofObj.uploaded_proof_url ?? proofObj.uploadedProofUrl ?? proofObj.url;
    const humanLabel = proofLabelMap[label] ?? label;

    return (
      <div key={key} className="flex items-center gap-2">
        <div className="w-32 h-18 bg-gray-100 rounded overflow-hidden border flex items-center justify-center shrink-0">

          {thumb ? (
            <img src={thumb} alt={`${humanLabel} thumbnail`} className="w-full h-full object-cover" />
          ) : url && isVideoUrl(url) ? (
            <video className="w-full h-full object-cover" preload="metadata" muted>
              <source src={url} />
            </video>
          ) : url ? (
            <img src={url} alt={humanLabel} className="w-full h-full object-cover" />
          ) : (
            <div className="text-xs text-gray-400">No preview</div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">{humanLabel}</div>
          <div className="text-xs text-gray-500">{ }</div>
        </div>

        <div className="flex-none">
          <button
            onClick={(e) => { e.stopPropagation(); openProofFresh(claimId, label); }}
            className="inline-flex items-center gap-2 px-3 py-1 border rounded bg-white text-sm"
            aria-label={`View ${humanLabel}`}
          >
            <Eye className="w-4 h-4" /> View
          </button>
        </div>
      </div>
    );
  };

  const copyToClipboard = async (text) => {
    try {
      if (!navigator?.clipboard) {
        const ta = document.createElement('textarea');
        ta.value = text ?? '';
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        return true;
      }
      await navigator.clipboard.writeText(text ?? '');
      return true;
    } catch (e) {
      console.warn('copy failed', e);
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-2 sm:pt-2 pb-4 px-2 sm:px-2">
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Header */}
        <div className="col-span-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Claims Dashboard</h3>
            <p className="text-sm text-gray-500">View, discuss and manage claims. Use Search to query.</p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search AWB, Claim ID, Order ref, description..."
              className="w-full sm:w-80"
            />
            <Button
              onClick={() => {
                setSearchQuery(searchText);
                setTimeout(() => fetchClaims(), 0);
              }}
              variant="outline"
            >
              Search
            </Button>

            <Button onClick={onRefresh} variant="outline">Refresh</Button>

            <Button onClick={() => setUploadModalOpen(true)} variant="primary" className="flex items-center gap-2">
              <PlusCircle size={16} /> New Claim
            </Button>
          </div>
        </div>

        {/* Left: table */}
        <div className="col-span-12 lg:col-span-8">
          <div className="rounded-xl bg-white shadow-sm border h-full flex flex-col min-h-[56vh]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">AWB</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Proofs</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {tableLoading ? (
                    <>
                      <SkeletonRow /><SkeletonRow /><SkeletonRow />
                    </>
                  ) : displayedClaims.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-sm text-gray-500">
                        No claims found. Click{" "}
                        <button onClick={() => setUploadModalOpen(true)} className="text-indigo-600 underline">
                          Create claim
                        </button>.
                      </td>
                    </tr>
                  ) : (
                    displayedClaims.map((c) => {
                      const isSelected = selectedClaim?.claim_id === c.claim_id;
                      const rawProofs = proofsForClaim[c.claim_id];
                      const proofs = (rawProofs && Object.keys(rawProofs).length > 0) ? rawProofs : (c.__raw ?? {});
                      const proofKeys = proofTypesForClaim(proofs);
                      const proofCount = proofKeys.length;

                      return (
                        <tr
                          key={c.claim_id}
                          className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-indigo-50" : ""}`}
                          onClick={() => selectAndOpenDetails(c)}
                        >
                          {/* AWB / Created */}
                          <td className="px-4 py-3 align-top max-w-xs">
                            <div className="text-sm font-medium text-gray-800">{c.awb_number ?? "—"}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {formatDate(c.created_date_time)}
                            </div>
                          </td>

                          {/* Claim Type */}
                          <td className="px-4 py-3 align-top">
                            <div
                              className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-medium ${c.claim_type === "WEIGHT_DISPUTE"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-indigo-50 text-indigo-700"
                                }`}
                            >
                              {c.claim_type || "—"}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3 align-top">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(c.claim_status)}
                              <div className="text-sm">{c.claim_status}</div>
                            </div>
                          </td>

                          {/* Proofs */}
                          <td className="px-4 py-3 align-top">
                            {proofCount === 0 ? (
                              <div className="text-xs text-gray-400">No proofs</div>
                            ) : (
                              <div className="flex flex-wrap items-center gap-1">
                                {proofKeys.map((key) => {
                                  const proofObj = proofs[key];
                                  const uploadId =
                                    proofObj?.upload_id ??
                                    proofObj?.uploadId ??
                                    proofObj?.claim_proof_object_key?.split("/")?.pop();

                                  return (
                                    <button
                                      key={key}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const merchantId = getMerchantId?.();
                                          const url = API_URLS.getUploadRefreshClaimPresignedUrlByUploadId(uploadId, merchantId);
                                          if (!url) {
                                            alert("Invalid upload ID or URL.");
                                            return;
                                          }

                                          const resp = await apiClient.get(url, { headers: getAuthHeaders(), timeout: 20000 });
                                          const refreshed = resp.data?.data ?? resp.data;

                                          if (refreshed?.url) {
                                            window.open(refreshed.url, "_blank", "noopener,noreferrer");
                                          } else {
                                            alert("No valid URL returned for proof.");
                                          }
                                        } catch (err) {
                                          console.error("Failed to refresh proof URL", err);
                                          alert("Could not refresh proof URL.");
                                        }
                                      }}

                                      title={`View ${proofLabelMap[key] ?? key}`}
                                      className="inline-flex items-center gap-1 border rounded px-2 py-1 text-xs bg-white hover:bg-gray-50"
                                    >
                                      {isVideoUrl(proofObj?.uploaded_proof_url ?? "") ? "🎥" : "🖼"}
                                      <span>{proofLabelMap[key] ?? key}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3 text-right align-top">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                title="Conversation"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openConversation(c);
                                }}
                                className="p-2 rounded hover:bg-gray-50"
                              >
                                <MessageSquare className="w-4 h-4 text-indigo-600" />
                              </button>

                              <button
                                title="Delete Claim"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteClaim(c.claim_id);
                                }}
                                className="p-2 rounded hover:bg-red-50"
                              >
                                {deletingId === c.claim_id ? (
                                  <Loader2 className="animate-spin w-4 h-4 text-red-600" />
                                ) : (
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* footer */}
            <div className="px-3 py-2 flex items-center justify-between border-t bg-white flex-col sm:flex-row gap-2">
              <div className="text-sm text-gray-600">
                Showing <strong>{displayedClaims.length}</strong> result{displayedClaims.length !== 1 ? "s" : ""}.
              </div>

              <div className="flex items-center gap-3">
                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="rounded border px-3 py-1 text-sm">
                  {ROWS_PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>

                <div className="text-sm text-gray-600">Page No.</div>
                <input type="number" value={offset} onChange={(e) => setOffset(Math.max(0, Number(e.target.value || 0)))} className="w-20 rounded border px-2 py-1 text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Right detail panel */}
        <div id="claim-detail-panel" className="col-span-12 lg:col-span-4">
          <div className="sticky top-20">
            <div className="rounded-xl bg-white shadow-sm border p-4 min-h-[200px] max-h-[calc(100vh-140px)] overflow-auto">
              {!selectedClaim ? (
                <div className="text-center text-gray-500 py-8">
                  <div className="text-sm font-medium mb-2">No claim selected</div>
                  <div className="text-xs mb-3">Select a claim to view details, proofs and conversation.</div>
                  <div className="flex justify-center gap-2">
                    <Button onClick={() => setUploadModalOpen(true)} variant="primary">New Claim</Button>
                    <Button onClick={onRefresh} variant="outline">Refresh</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {/* AWB PRIMARY */}
                      <div className="text-sm text-gray-500">AWB</div>
                      <div className="text-lg font-semibold break-words">{selectedClaim.awb_number ?? "—"}</div>
                      <div className="text-xs text-gray-400 mt-1">Claim: {selectedClaim.claim_id}</div>
                    </div>

                    <div className="flex items-start gap-2">
                      <div className="text-xs text-gray-500">{selectedClaim.claim_status}</div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500">Description</label>
                    <EditableTextarea
                      value={selectedClaim.claim_description ?? ""}
                      placeholder="Short description..."
                      onSave={async (v) => { await saveClaimEdits(selectedClaim.claim_id, { claim_description: v }); }}
                    />
                  </div>

                  <div className="bg-gray-50 rounded p-3 border">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">Items</div>
                      <div className="text-xs text-gray-400">{(selectedClaim.items || []).length} item(s)</div>
                    </div>

                    <div className="mt-2 space-y-2 max-h-40 overflow-auto">
                      {(selectedClaim.items || []).map((it, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="shrink-0 w-2.5 h-2.5 rounded-full bg-indigo-600 mt-2" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{it.item_name ?? it.itemName ?? it.sku_id ?? it.skuId}</div>
                            <div className="text-xs text-gray-500">SKU: {it.sku_id ?? it.skuId} • Qty: {it.units ?? it.quantity ?? 1}</div>
                          </div>
                        </div>
                      ))}
                      {(!selectedClaim.items || selectedClaim.items.length === 0) && <div className="text-xs text-gray-400">No item details</div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded p-3 border">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm">Proofs</div>
                      <div className="text-xs text-gray-400">{proofsLoading[selectedClaim.claim_id] ? "Loading..." : ""}</div>
                    </div>

                    <div className="mt-3 space-y-3">
                      {renderProofPreview(selectedClaim.claim_id, "unpacked_video", proofsForClaim[selectedClaim.claim_id]?.unpacked_video ?? selectedClaim.unpacked_video)}
                      {renderProofPreview(selectedClaim.claim_id, "packaging_video", proofsForClaim[selectedClaim.claim_id]?.packaging_video ?? selectedClaim.packaging_video)}
                      {renderProofPreview(selectedClaim.claim_id, "proof_of_delivery_photo", proofsForClaim[selectedClaim.claim_id]?.proof_of_delivery_photo ?? selectedClaim.proof_of_delivery_photo)}
                      {!((proofsForClaim[selectedClaim.claim_id]?.unpacked_video ?? selectedClaim.unpacked_video) || (proofsForClaim[selectedClaim.claim_id]?.packaging_video ?? selectedClaim.packaging_video) || (proofsForClaim[selectedClaim.claim_id]?.proof_of_delivery_photo ?? selectedClaim.proof_of_delivery_photo)) && (
                        <div className="text-sm text-gray-500">No proofs attached. Use New Claim flow to upload a proof.</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded p-3 border">
                    <div className="font-medium text-sm">Activity</div>
                    <div className="mt-2 space-y-2 text-sm text-gray-600 max-h-40 overflow-auto">
                      {(selectedClaim.claim_dispute_history || []).map((h, idx) => (
                        <div key={idx} className="text-xs">
                          <div className="text-gray-800">{h.note}</div>
                          <div className="text-gray-400">{h.action_taken_by} • {formatDate(h.action_taken_on)}</div>
                        </div>
                      ))}
                      {(!selectedClaim.claim_dispute_history || selectedClaim.claim_dispute_history.length === 0) && <div className="text-xs text-gray-400">No activity yet.</div>}
                    </div>

                    <div className="mt-3 border-t pt-3 flex gap-2">
                      <Button variant="outline" onClick={() => openConversation(selectedClaim)}>Open Conversation</Button>
                      <Button variant="ghost" onClick={() => fetchProofsForClaim(selectedClaim.claim_id)}>Refresh Proofs</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ClaimUploadModal
        open={uploadModalOpen}
        onClose={(res) => { setUploadModalOpen(false); setSelectedClaim(null); if (res?.success) onRefresh(); }}
        shipment={null}
        defaultItem={null}
      />

      {/* Conversation slide-over */}
      {/* Conversation slide-over */}
      <AnimatePresence>
        {convoOpen && (
          <motion.div initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }} className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <div className="font-medium">{convoClaim?.claim_id ?? "Conversation"}</div>
                  <div className="text-xs text-gray-500">{convoClaim?.awb_number}</div>
                </div>
                <div>
                  <button onClick={() => { setConvoOpen(false); setConvoClaim(null); setConversations([]); }} className="p-2 rounded hover:bg-gray-50">Close</button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-4 space-y-3">
                {convoLoading ? (
                  <div className="text-sm text-gray-500">Loading messages…</div>
                ) : conversations.length === 0 ? (
                  <div className="text-sm text-gray-500">No messages yet. Start the conversation.</div>
                ) : (
                  conversations.map((m) => {
                    const isMe = (m.authorRole || "").toUpperCase() === "MERCHANT";
                    return (
                      <div key={m.messageId} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-xs p-3 rounded-lg ${isMe ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-800"}`}>
                          <div className="text-sm whitespace-pre-wrap break-words">{m.text || "(no text)"}</div>
                          <div className="text-xs mt-1 text-gray-300">{formatDate(m.createdAt)}</div>
                          {m.failed && <div className="text-xs text-red-100 mt-1">Failed to send</div>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="p-4 border-t">
                <div className="flex items-center gap-2">
                  <input value={convoText} onChange={(e) => setConvoText(e.target.value)} placeholder="Write a message..." className="flex-1 rounded border px-3 py-2" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendConversationMessage(convoText); } }} />
                  <button onClick={() => sendConversationMessage(convoText)} className="rounded bg-indigo-600 text-white px-4 py-2">Send</button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

function EditableField({ value = "", placeholder = "", onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => setVal(value), [value]);

  const commit = async () => {
    if (onSave && val !== value) await onSave(val);
    setEditing(false);
  };

  return editing ? (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="w-full rounded border px-2 py-1 text-sm"
      placeholder={placeholder}
    />
  ) : (
    <div className="flex items-center gap-2">
      <div className="text-sm text-gray-700 break-words">{val || <span className="text-gray-400">{placeholder}</span>}</div>
      <button onClick={() => setEditing(true)} className="text-xs text-indigo-600 hover:underline">Edit</button>
    </div>
  );
}

function EditableTextarea({ value = "", placeholder = "", onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => setVal(value), [value]);

  const commit = async () => {
    if (onSave && val !== value) await onSave(val);
    setEditing(false);
  };

  return editing ? (
    <textarea
      autoFocus
      rows={4}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="w-full rounded border px-2 py-1 text-sm"
      placeholder={placeholder}
    />
  ) : (
    <div className="flex items-start gap-2">
      <div className="text-sm text-gray-700 break-words whitespace-pre-wrap">{val || <span className="text-gray-400">{placeholder}</span>}</div>
      <button onClick={() => setEditing(true)} className="text-xs text-indigo-600 hover:underline">Edit</button>
    </div>
  );
}
